import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useLocation } from 'react-router-dom';

import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { Container, Grid, Paper, units } from '@hbf/dsl';
import type { Theme } from '@hbf/dsl';

import { SearchFiltersProvider } from 'ha/pages/Search/SearchFiltersProvider';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { useIntl } from 'ha/i18n';

import { getRoute } from 'ha/modules/Hermes/selectors';
import { SearchMetaContainer as SearchMeta } from '../containers/SearchMeta';
import { LoadableSearchMap as SearchMap } from './SearchMap.lazy';
import { SearchResultsContainer as SearchResults } from '../containers/SearchResults';
import { MobileMapContainer as MobileMap } from '../containers/MobileMap';
import { SearchResultsLoader } from './SearchResultsLoader';
import { SearchPageCrawl } from './SearchPageCrawl';

import { LoadableSearchFiltersSidebar } from './SearchFilters/SearchFiltersSidebar.lazy';
import { SearchOverview } from './SearchOverview';
import { getMapViewState, getCountryCode } from '../selectors';
import { MapViewState } from '../utils';
import { SearchCityAbout, SearchCityLinks } from './SearchSEO';
import { SearchBerlinSEO } from './SearchBerlinSEO';
import { NEW_ABOUT_SEO_CONFIG, sidebarWidth } from '../constants';

const filtersHeightWithLeftPanelFilters = 68;
const BERLIN_PATHNAME = '/s/Berlin--Germany';

const useStyles = makeStyles()((theme: Theme) => ({
  suggestionBar: {
    position: 'sticky',
    width: '100%',
    bottom: 0,
    zIndex: 4,
    fallbacks: {
      position: 'fixed',
    },
  },
  mapPaper: {
    position: 'sticky',
    zIndex: 3,
    overflow: 'hidden',
    boxShadow: 'none',
    height: `calc(100vh - ${
      theme.customSpacing.navHeightLg + filtersHeightWithLeftPanelFilters
    }px)`,
    top: `${
      theme.customSpacing.navHeightLg + filtersHeightWithLeftPanelFilters
    }px`,
    boxSizing: 'border-box',
    border: units.border(1, 'solid', theme.palette.grey[300]),
    borderRadius: '4px',
  },
  filters: {
    width: units.pxToRem(sidebarWidth),
    marginRight: '1.5rem',
  },
  width0: {
    maxWidth: 0,
    height: 0,
    overflow: 'hidden',
  },
  listingsContainer: {
    position: 'relative',
  },
}));

export const SearchLayout: React.FC = () => {
  const location = useLocation();
  const { md } = useMediaQuery();
  const { classes, cx } = useStyles();
  const { showNewAboutSEO } = useFeatureFlags();
  const { urlResolver } = useIntl();

  const mapViewState = useSelector(getMapViewState);
  const countryCode = useSelector(getCountryCode);
  const route = useSelector(getRoute);
  const isSearchCityPage = urlResolver.isSearchCityPage(location.pathname);

  const showSEOContent =
    showNewAboutSEO === 'on' &&
    isSearchCityPage &&
    NEW_ABOUT_SEO_CONFIG.countryCodes.includes(countryCode.toLowerCase()) &&
    mapViewState !== MapViewState.fullMap;

  const showNewBerlinSEO =
    route?.pathname?.toLowerCase() === BERLIN_PATHNAME.toLowerCase();

  return (
    <SearchFiltersProvider instantaneousFiltering>
      <Container
        disableGutters={!md}
        maxWidth={
          md && mapViewState === MapViewState.fullMap ? false : undefined
        }
      >
        <SearchOverview />
        <Grid container wrap="nowrap">
          {md && (
            <div className={classes.filters}>
              <LoadableSearchFiltersSidebar />
            </div>
          )}
          <SearchMeta />
          <SearchPageCrawl />

          <Grid container item xs>
            {!(md && mapViewState === MapViewState.fullMap) && (
              <Grid
                container
                direction="column"
                wrap="nowrap"
                item
                md={12}
                className={cx(
                  classes.listingsContainer,
                  mapViewState === MapViewState.fullMap && md && classes.width0,
                )}
                data-test-locator="Search Listings Grid"
              >
                <SearchResultsLoader />
                <SearchResults />
              </Grid>
            )}

            {!md && <MobileMap />}

            {md && mapViewState !== MapViewState.noMap && (
              <Grid item md={12} data-test-locator="Search Listings Map">
                <Paper className={classes.mapPaper}>
                  <SearchMap />
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Container>

      {showNewBerlinSEO && <SearchBerlinSEO />}

      {showSEOContent && (
        <React.Fragment>
          <SearchCityAbout />
          <SearchCityLinks />
        </React.Fragment>
      )}
    </SearchFiltersProvider>
  );
};
